import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ArrivalState } from '@/lib/use-saved-filters';
import { USER_GROUP_SLUGS, type StaffNarrowing, type UserGroupSlug } from '@/lib/user-groups';

/**
 * The Account lead filter starts on the SIGNED-IN PERSON.
 *
 * Owner's instruction: *"in companies, quotes, deals, invoices if the logged in
 * user is present in the account leads filter dropdown then always auto select
 * it by default everytime"* — a bare `/deals`, `/quotes` or `/invoices` shows
 * what is attached to whoever is looking, rather than the whole pipeline.
 *
 * ⚠️ **ONE copy, deliberately.** This shipped on `/deals` first and was lifted
 * here unchanged when the other pages wanted it. Four copies would agree the day
 * they were written and drift on the first correction — and what would drift is
 * the *three-states* logic below, whose failure modes are all silent.
 *
 * ## It writes the resolved id INTO the URL, once
 *
 * ⚠️ **The naive version — "when the URL names no lead, filter to me" — cannot
 * be cleared at all**: the user clears it, the param disappears, the condition is
 * true again, the default re-applies, and the control reads as broken. Same
 * mechanism as `/projects`' current-cycle default (`defaultCycleSettled` in
 * `ProjectsPage.tsx`), distinguishing the states that look identical in the URL a
 * moment after the fact:
 *
 *   1. the account already answered (saved filters)  -> stand down
 *   2. a link names ANY filter                       -> stand down; THE LINK WINS
 *   3. the address already names a lead              -> stand down; it wins
 *   4. first arrival, nothing named                  -> seed, if there is evidence
 *   5. the user CLEARED it                           -> already settled; stays cleared
 *
 * A ref settles it, not state: nothing renders from "have we decided yet", and
 * the decision has to survive a re-render mid-flight.
 *
 * ⚠️ **"Once" means ONCE PER MOUNT, and clearing therefore holds for this visit
 * rather than for ever** — leaving the page and coming back re-seeds, exactly as
 * the cycle default does. Two things soften that and both are worth knowing
 * before somebody "fixes" it: the seed lands in the URL, so the very next
 * debounce persists it as a saved filter and the account then answers on later
 * arrivals; and a saved set naming ANY other filter settles this as `'seeded'`,
 * so a cleared lead sticks whenever anything else is saved. Only a completely
 * empty saved set re-seeds. Remembering a cleared filter across mounts would need
 * a stored flag — a second source of truth beside the URL, which is what
 * `useSavedFilters` exists to avoid.
 *
 * ## What it will NOT do
 *
 * ⛔ **It never writes a filter a page does not READ from the URL.** A page whose
 * account-lead filter lives in component state (`/companies`, whose funnel state
 * is `useTableFilters`' own `useState({})`) must not call this: the param would
 * be written, persisted by `useSavedFilters`, and silently ignored by the fetch —
 * the "saves cleanly, does nothing" trap this repo has shipped before. Moving
 * that page's funnel onto the URL comes first; this hook is the easy half.
 */

/**
 * Staff evidence, structurally a subset of `useUserOptions(true, true)`'s return
 * — so a call site passes `staff` straight through and cannot mis-wire it.
 *
 * ⚠️ **`groupsLoading` is not optional padding.** The membership test is only
 * meaningful once BOTH reads have answered; testing against a half-loaded
 * directory is how a member gets no default and a non-member gets one.
 */
export interface AccountLeadStaffEvidence {
  byGroup: Record<UserGroupSlug, StaffNarrowing>;
  /** `/users` is still out. */
  loading: boolean;
  /** The `/user-groups` master is still out. */
  groupsLoading: boolean;
}

/** Just enough of `useSavedFilters`' return to sequence against it. */
export interface AccountLeadArrival {
  seeding: boolean;
  arrival: ArrivalState | null;
}

/**
 * May this person be named as an account lead, on evidence actually in hand?
 *
 * `null` means **say nothing and seed nothing** — never "no".
 *
 * ⚠️ **Only when they are really in the `account_lead` group** — the same test,
 * on the same evidence, as `CompanyFormModal`'s seeded lead, and taken for a
 * reason of its own: **here the risk is pre-filling a filter that MATCHES
 * NOTHING**, and an empty list on arrival reads as "there are no quotes" rather
 * than as "none of them are yours". Somebody who leads nothing gets the whole
 * pipeline, which is exactly what they had before this existed.
 *
 * ⚠️ *SUPERSEDED 2026-09-18 — the CROSS-REFERENCE, and not this hook's own rule:
 * "taken for the same reason turned around. There the risk is pre-filling a
 * value the server refuses".* **`POST /companies` does not refuse a non-member's
 * default.** The group guard runs against the parsed body and the creator
 * default is merged in fifty lines later, so it is never checked; the form's own
 * block carries the measurement. That corrects the OTHER file's reasoning and
 * leaves this narrowing untouched — **a filter that matches nothing is wrong
 * whatever the server tolerates**, so the test here never depended on it.
 *
 * ⚠️ **`narrowed` is the load-bearing half.** When the group master is unreadable
 * or answers 404, `narrowStaffToGroup` hands back the WHOLE directory with
 * `narrowed: false` — so a membership test against it would pass for everybody on
 * the strength of a lookup that never resolved. **A list that has not been
 * narrowed is not evidence of membership**, and a lookup that failed must not
 * seed a filter. The two other unknown states need no test of their own: while
 * `/users` is out, and after it has failed, `users` is empty, so the match fails
 * and nothing is seeded.
 *
 * ⚠️ **Group membership is a PROXY for "leads anything", and not a perfect one —
 * said plainly rather than left to be discovered.** It answers *may this person be
 * NAMED as a lead*, not *does this person lead anything*, so a member who leads
 * nothing still gets an empty first screen. What makes that survivable — and what
 * makes this different from a hidden default — is that the seed is WRITTEN INTO
 * THE URL: the page shows whose list this is, and one click clears it. **The
 * honest fix if it ever bites is a server-side "leads anything" answer, never a
 * quieter default.**
 */
export function accountLeadSeedId(
  user: { id: number } | null,
  leadStaff: StaffNarrowing,
): string | null {
  if (!user) return null;
  if (!leadStaff.narrowed) return null;
  return leadStaff.users.some((row) => row.id === user.id) ? String(user.id) : null;
}

export interface DefaultAccountLeadOptions {
  /** The signed-in person, from `useAuth()`. `null` seeds nothing. */
  user: { id: number } | null;
  /** What `useUserOptions(enabled, true)` returns — the groups half is required. */
  staff: AccountLeadStaffEvidence;
  /** `useSavedFilters(...)`'s return — this hook sequences behind it. */
  savedFilters: AccountLeadArrival;
  /**
   * Defaults to `true`. `false` settles nothing and writes nothing, for a page
   * the reader may not read at all.
   */
  enabled?: boolean;
  /**
   * Fired only when a seed was actually written — the page's own pager reset.
   *
   * ⚠️ **A seed IS a filter change, so the pager must reset with it.** This hook
   * waits on a preferences read that can take seconds, so a reader may already
   * have paged, and page 4 of the whole pipeline is a page number for a different
   * result set: it comes back empty and reads as "no records". The house rule
   * says every filter change resets the pager, and `/users` shipped without it
   * once — that is exactly how it failed.
   */
  onSeeded?: () => void;
}

/** The query param. One spelling, and it is the same on all four pages. */
const ACCOUNT_LEAD_PARAM = 'accountLeadId';

export function useDefaultAccountLeadFilter(options: DefaultAccountLeadOptions): void {
  const { user, staff, savedFilters, enabled = true, onSeeded } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const leadStaff = staff.byGroup[USER_GROUP_SLUGS.accountLead];
  const seededLeadId = useMemo(() => accountLeadSeedId(user, leadStaff), [user, leadStaff]);

  /*
   * Latest-ref for the callback alone. `onSeeded` is an inline arrow at every
   * call site, so a fresh function each render; as a dependency it would re-run
   * this effect on every render until it settled, which is harmless and noisy —
   * and would be a real bug the day somebody's callback closed over state.
   */
  const latestOnSeeded = useRef(onSeeded);
  useEffect(() => {
    latestOnSeeded.current = onSeeded;
  });

  const settled = useRef(false);

  useEffect(() => {
    if (settled.current) return;
    if (!enabled) return;

    /*
     * ── STATE 1: the account already answered ────────────────────────────
     *
     * Two arrival writers put filters into the URL on these pages now, and they
     * must not race, or a reader gets whichever landed second. So this one
     * waits, and stands down when the other did anything:
     *
     *   - `seeding` — the preferences read is still out. It is bounded by the
     *     hook's own budget, so this cannot hang.
     *   - `'seeded'` — saved filters applied. **Including when they name no
     *     lead**, which is a person who cleared it: re-deriving the default over
     *     that is cleared-means-cleared failing from storage instead of from the
     *     URL.
     */
    if (savedFilters.seeding) return;
    /*
     * `'remembered'` (filter memory, owner 2026-10-16): the reader's own last
     * state on this page was restored, or their remembered CLEAR stands. Either
     * way they have answered, so a cleared lead is never re-seeded. A reader
     * with nothing remembered still gets the first-visit seed below.
     */
    if (savedFilters.arrival === 'seeded' || savedFilters.arrival === 'remembered') {
      settled.current = true;
      return;
    }

    /*
     * ── STATE 2: a link naming ANY filter wins entirely ───────────────────
     *
     * ⚠️ **Stricter than `/projects`, deliberately, and the difference is the
     * KIND of default.** A cycle is a page-level convention; a PERSON filter is a
     * claim about whose pipeline this is. A colleague sending `/deals?stageId=3`
     * and the recipient seeing only their own stage-3 deals is one URL rendering
     * differently for two people — precisely what the saved-filters ruling "a
     * link wins entirely" exists to prevent. So a link naming any filter key at
     * all settles this without seeding.
     */
    if (savedFilters.arrival === 'link-wins') {
      settled.current = true;
      return;
    }

    /*
     * ── STATE 3: the address already names a lead. It wins, untouched. ────
     *
     * PRESENCE, not truthiness, exactly as `namesAnyKey` reads it: a bare
     * `?accountLeadId=` is somebody saying "no lead", and it is still somebody
     * saying something.
     */
    if (searchParams.has(ACCOUNT_LEAD_PARAM)) {
      settled.current = true;
      return;
    }

    /*
     * The evidence is not in yet. Bounded by two requests rather than a timer,
     * and NOTHING on these pages waits on this effect — the list and the board
     * are already loading unfiltered — so the worst case is a filter arriving a
     * moment after the first paint, never a blank screen.
     */
    if (staff.loading || staff.groupsLoading) return;

    /*
     * Settled BEFORE the seed is applied, and settled even when nothing is
     * seeded: one attempt per mount either way. Without it, somebody who leads
     * nothing would have the question re-asked on every render.
     */
    settled.current = true;
    if (seededLeadId === null) return;

    /*
     * `{ replace: true }`, like every other filter write in this app — the seed
     * is not a navigation the reader performed, and leaving it in history would
     * make Back walk through a URL rewrite they never asked for.
     */
    const next = new URLSearchParams(searchParams);
    next.set(ACCOUNT_LEAD_PARAM, seededLeadId);
    setSearchParams(next, { replace: true });
    latestOnSeeded.current?.();
  }, [
    enabled,
    savedFilters.seeding,
    savedFilters.arrival,
    searchParams,
    setSearchParams,
    seededLeadId,
    staff.loading,
    staff.groupsLoading,
  ]);
}
